import { useState, useEffect } from 'react';
import { clearSharedCache, fetchMyProfile } from '@/lib/supabaseHelpers';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MapPin, Phone } from 'lucide-react';

const TELANGANA_DISTRICTS = [
  'Adilabad', 'Bhadradri Kothagudem', 'Hanamkonda', 'Hyderabad', 'Jagtial',
  'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy',
  'Karimnagar', 'Khammam', 'Kumuram Bheem Asifabad', 'Mahabubabad',
  'Mahbubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu',
  'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad',
  'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy',
  'Siddipet', 'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal',
  'Yadadri Bhuvanagiri',
];

export default function DistrictPromptDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [district, setDistrict] = useState('');
  const [phone, setPhone] = useState('');
  const [needDistrict, setNeedDistrict] = useState(false);
  const [needPhone, setNeedPhone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchMyProfile(user.id)
      .then((data) => {
        if (!data) return;
        const missingDistrict = !data.district;
        const missingPhone = !(data as any).phone;
        setNeedDistrict(missingDistrict);
        setNeedPhone(missingPhone);
        if (missingDistrict || missingPhone) setOpen(true);
      });
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    if (needDistrict && !district) return;
    if (needPhone) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length !== 10) {
        toast.error('Enter a valid 10-digit phone number');
        return;
      }
    }
    setSaving(true);
    const updates: any = {};
    if (needDistrict) updates.district = district;
    if (needPhone) updates.phone = phone.replace(/\D/g, '');
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', user.id);
    setSaving(false);
    if (error) {
      toast.error('Failed to save profile');
    } else {
      toast.success('Profile updated!');
      clearSharedCache('profile:');
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-sm" onPointerDownOutside={e => e.preventDefault()}>
        <DialogHeader>
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-2">
            {needPhone && !needDistrict ? <Phone className="w-5 h-5 text-primary" /> : <MapPin className="w-5 h-5 text-primary" />}
          </div>
          <DialogTitle className="text-center">Complete Your Profile</DialogTitle>
          <DialogDescription className="text-center">
            Please provide the missing details below to continue using the app.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {needDistrict && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> District</Label>
              <Select value={district} onValueChange={setDistrict}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose your district" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TELANGANA_DISTRICTS.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {needPhone && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> Phone Number</Label>
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </div>
          )}
          <Button
            className="w-full"
            disabled={saving || (needDistrict && !district) || (needPhone && phone.replace(/\D/g, '').length !== 10)}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
